"use client";

import type { CSSProperties, RefObject } from "react";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";

import { toast } from "@/app/_providers/toast";
import { parseUrlInput } from "@/app/_utilities/url";
import { Button } from "@ui/components/core/Button";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { cn } from "@ui/lib/utils";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconChainLink3 from "@ui/central-icons/icons/IconChainLink3";
import IconArrowUp from "@ui/central-icons/icons/IconArrowUp";
import IconCrossLarge from "@ui/central-icons/icons/IconCrossLarge";
import { UPLOAD_VIEW_TRANSITION_MS } from "./constants";

const urlInputTransition = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 4 },
  transition: { duration: UPLOAD_VIEW_TRANSITION_MS / 1000, ease: "easeOut" },
} as const;

const TRIGGER_MORPH_DURATION = 0.34;
const TRIGGER_COLLAPSED_WIDTH = 40;
const TRIGGER_ICON_SIZE = 14;
const TRIGGER_CONTENT_OFFSET = 6;
const TRIGGER_HORIZONTAL_PADDING = 32;
const TRIGGER_ICON_RIGHT_OFFSET = (TRIGGER_COLLAPSED_WIDTH - TRIGGER_ICON_SIZE) / 2;
const PASTE_LINK_ROTATION = 8;
const UPLOAD_FILE_ROTATION = 4;
const MENU_OPTION_Y_OFFSET = 12;
const triggerWidthSpring = { type: "spring", visualDuration: TRIGGER_MORPH_DURATION, bounce: 0.28 } as const;
const triggerContentEnterTransition = { duration: 0.16, ease: "easeOut" } as const;
const triggerContentExitTransition = { duration: 0.16, ease: "easeIn" } as const;
const triggerIconExitTransition = { duration: 0.1, ease: "easeIn" } as const;
const menuOptionSpringTransition = { type: "spring", visualDuration: 0.34, bounce: 0.36 } as const;
const menuOptionOpacityTransition = { duration: UPLOAD_VIEW_TRANSITION_MS / 1000 } as const;
const menuOptionStagger = 0.04;
const createMenuOptionTransition = (delay = 0) =>
  ({
    opacity: { ...menuOptionOpacityTransition, delay },
    rotate: { ...menuOptionSpringTransition, delay },
    y: { ...menuOptionSpringTransition, delay },
  }) as const;
const triggerStyleVars = {
  "--stt-trigger-collapsed-width": `${TRIGGER_COLLAPSED_WIDTH}px`,
  "--stt-trigger-icon-right": `${TRIGGER_ICON_RIGHT_OFFSET}px`,
  "--stt-trigger-icon-size": `${TRIGGER_ICON_SIZE}px`,
} as CSSProperties;

const MotionButton = m.create(Button);

export type UploadControlsView = "closed" | "menu" | "urlInput";

interface STTFloatingCardUploadControlsProps {
  allowUrlTranscription: boolean;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onClosedWidthChange?: (width: number) => void;
  onExitComplete?: () => void;
  view: UploadControlsView;
  onUrlSubmit: (url: string) => void | Promise<void>;
  onViewChange: (view: UploadControlsView) => void;
}

export function STTFloatingCardUploadControls({
  allowUrlTranscription,
  fileInputRef,
  onClosedWidthChange,
  onExitComplete,
  view,
  onUrlSubmit,
  onViewChange,
}: STTFloatingCardUploadControlsProps) {
  const urlInputRef = useRef<HTMLInputElement | null>(null);
  const tForms = useTranslations("ClientKeys.forms");

  const handleUploadFile = () => {
    fileInputRef.current?.click();
  };

  const handleUrlTranscribeClick = () => {
    const raw = urlInputRef.current?.value?.trim();
    if (!raw) return;

    const url = parseUrlInput(raw);
    if (!url) {
      toast.error(tForms("validation.url"));
      return;
    }

    void onUrlSubmit(url);
    onViewChange("closed");
    if (urlInputRef.current) {
      urlInputRef.current.value = "";
    }
  };

  const isUrlInputOpen = view === "urlInput";
  const isMenuOpen = view === "menu";

  return (
    <AnimatePresence mode='wait' initial={false} onExitComplete={onExitComplete}>
      {isUrlInputOpen ? (
        <m.div key='url-input' className='tw-flex tw-min-w-0 tw-flex-1' {...urlInputTransition}>
          <STTFloatingCardUrlInput
            inputRef={urlInputRef}
            onSubmit={handleUrlTranscribeClick}
            onCancel={() => onViewChange("closed")}
          />
        </m.div>
      ) : (
        <div key='upload-controls' className='tw-flex tw-flex-col tw-items-end tw-gap-1.5' style={triggerStyleVars}>
          <STTFloatingCardUploadMenuOptions
            isMenuOpen={isMenuOpen}
            onUploadFile={handleUploadFile}
            onPasteLink={() => onViewChange("urlInput")}
          />
          <STTFloatingCardUploadTrigger
            allowUrlTranscription={allowUrlTranscription}
            isMenuOpen={isMenuOpen}
            onClosedWidthChange={onClosedWidthChange}
            onClose={() => onViewChange("closed")}
            onOpenMenu={() => onViewChange("menu")}
            onUploadFile={handleUploadFile}
          />
        </div>
      )}
    </AnimatePresence>
  );
}

interface STTFloatingCardUploadTriggerProps {
  allowUrlTranscription: boolean;
  isMenuOpen: boolean;
  onClosedWidthChange?: (width: number) => void;
  onClose: () => void;
  onOpenMenu: () => void;
  onUploadFile: () => void;
}

function STTFloatingCardUploadTrigger({
  allowUrlTranscription,
  isMenuOpen,
  onClosedWidthChange,
  onClose,
  onOpenMenu,
  onUploadFile,
}: STTFloatingCardUploadTriggerProps) {
  const t = useTranslations("ClientKeys.STTHero");
  const label = allowUrlTranscription ? t("add_file") : t("upload_file");
  const labelMeasureRef = useRef<HTMLSpanElement | null>(null);
  const [closedWidth, setClosedWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!labelMeasureRef.current) return;

    const updateClosedWidth = () => {
      const labelWidth = labelMeasureRef.current?.getBoundingClientRect().width ?? 0;
      setClosedWidth(Math.ceil(labelWidth + TRIGGER_HORIZONTAL_PADDING));
    };

    updateClosedWidth();
    const resizeObserver = new ResizeObserver(updateClosedWidth);
    resizeObserver.observe(labelMeasureRef.current);

    return () => resizeObserver.disconnect();
  }, [label]);

  useEffect(() => {
    if (!closedWidth) return;
    onClosedWidthChange?.(closedWidth);
  }, [closedWidth, onClosedWidthChange]);

  const handleClick = () => {
    if (isMenuOpen) {
      onClose();
      return;
    }

    if (allowUrlTranscription) {
      onOpenMenu();
      return;
    }

    onUploadFile();
  };

  const targetWidth = isMenuOpen ? TRIGGER_COLLAPSED_WIDTH : (closedWidth ?? undefined);

  return (
    <MotionButton
      type='button'
      className={cn(
        "tw-pointer-events-auto tw-relative tw-min-w-0 tw-shrink-0 tw-overflow-hidden",
        isMenuOpen && "tw-w-[var(--stt-trigger-collapsed-width)]",
      )}
      variant='elevated'
      size='md'
      initial={false}
      animate={targetWidth ? { width: targetWidth } : undefined}
      transition={{ width: triggerWidthSpring }}
      aria-label={isMenuOpen ? t("cancel") : undefined}
      onClick={handleClick}
    >
      <span
        ref={labelMeasureRef}
        className={cn(
          "tw-pointer-events-none tw-shrink-0 tw-whitespace-nowrap tw-opacity-0",
          isMenuOpen && "tw-absolute tw-invisible",
        )}
        aria-hidden='true'
      >
        {label}
      </span>
      <AnimatePresence initial={false}>
        {isMenuOpen ? (
          <m.span
            key='close-icon'
            aria-hidden='true'
            className='tw-pointer-events-none tw-absolute tw-right-[var(--stt-trigger-icon-right)] tw-top-1/2 tw-flex tw-size-[var(--stt-trigger-icon-size)] -tw-translate-y-1/2 tw-items-center tw-justify-center'
            initial={{ opacity: 0, filter: "blur(2px)" }}
            animate={{ opacity: 1, filter: "blur(0px)", transition: triggerContentEnterTransition }}
            exit={{
              opacity: 0,
              filter: "blur(2px)",
              transition: triggerIconExitTransition,
            }}
          >
            <m.span
              className='tw-flex tw-size-[var(--stt-trigger-icon-size)] tw-items-center tw-justify-center'
              initial={{ x: TRIGGER_CONTENT_OFFSET * -2 }}
              animate={{ x: 0 }}
              exit={{ x: TRIGGER_CONTENT_OFFSET * -2 }}
            >
              <IconCrossLarge className={cn(iconStrokeFix, "tw-size-[var(--stt-trigger-icon-size)]")} />
            </m.span>
          </m.span>
        ) : (
          <m.span
            key='label'
            className='tw-pointer-events-none tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-whitespace-nowrap'
            initial={{ opacity: 0, filter: "blur(2px)" }}
            animate={{ opacity: 1, filter: "blur(0px)", transition: triggerContentEnterTransition }}
            exit={{
              opacity: 0,
              filter: "blur(2px)",
              transition: triggerContentExitTransition,
            }}
          >
            <m.span
              className='tw-inline-block'
              initial={{ x: TRIGGER_CONTENT_OFFSET }}
              animate={{ x: 0 }}
              exit={{ x: TRIGGER_CONTENT_OFFSET }}
            >
              {label}
            </m.span>
          </m.span>
        )}
      </AnimatePresence>
    </MotionButton>
  );
}

interface STTFloatingCardUploadMenuOptionsProps {
  isMenuOpen: boolean;
  onUploadFile: () => void;
  onPasteLink: () => void;
}

function STTFloatingCardUploadMenuOptions({
  isMenuOpen,
  onUploadFile,
  onPasteLink,
}: STTFloatingCardUploadMenuOptionsProps) {
  const t = useTranslations("ClientKeys.STTHero");
  const menuOptions = isMenuOpen
    ? [
        <MotionButton
          key='paste-link'
          className='tw-pointer-events-auto tw-origin-right'
          variant='elevated'
          size='md'
          icon={<IconChainLink3 className={cn(iconStrokeFix, "tw-size-3.5")} />}
          iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
          initial={{ opacity: 0, rotate: 0, y: MENU_OPTION_Y_OFFSET }}
          animate={{ opacity: 1, rotate: PASTE_LINK_ROTATION, y: 0 }}
          exit={{ opacity: 0, rotate: 0, y: MENU_OPTION_Y_OFFSET }}
          transition={createMenuOptionTransition()}
          onClick={onPasteLink}
        >
          {t("paste_link")}
        </MotionButton>,
        <MotionButton
          key='upload-file'
          className='tw-pointer-events-auto tw-origin-right tw-mr-2'
          variant='elevated'
          size='md'
          icon={<IconArrowUp className={cn(iconStrokeFix, "tw-size-3.5")} />}
          iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
          initial={{ opacity: 0, rotate: 0, y: MENU_OPTION_Y_OFFSET }}
          animate={{ opacity: 1, rotate: UPLOAD_FILE_ROTATION, y: 0 }}
          exit={{ opacity: 0, rotate: 0, y: MENU_OPTION_Y_OFFSET }}
          transition={createMenuOptionTransition(menuOptionStagger)}
          onClick={onUploadFile}
        >
          {t("upload_file")}
        </MotionButton>,
      ]
    : null;

  return <AnimatePresence initial={false}>{menuOptions}</AnimatePresence>;
}

interface STTFloatingCardUrlInputProps {
  inputRef: RefObject<HTMLInputElement | null>;
  onSubmit: () => void;
  onCancel: () => void;
}

function STTFloatingCardUrlInput({ inputRef, onSubmit, onCancel }: STTFloatingCardUrlInputProps) {
  const t = useTranslations("ClientKeys.STTHero");

  return (
    <div className='tw-pointer-events-auto tw-flex tw-min-w-0 tw-w-full tw-flex-col tw-gap-3'>
      <div className='tw-relative tw-flex tw-items-center'>
        <IconChainLink3
          className={cn(
            iconStrokeFix,
            "tw-absolute tw-left-4 tw-size-4 tw-shrink-0 tw-text-black tw-pointer-events-none",
          )}
        />
        <input
          ref={inputRef}
          type='url'
          className='tw-w-full tw-h-11 tw-rounded-full tw-border-0 tw-bg-white tw-pl-11 tw-pr-4 tw-type-sm tw-text-black placeholder:tw-text-black/30 tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_1px_1px_0px_rgba(0,0,0,0.04),0px_2px_4px_0px_rgba(0,0,0,0.04)] tw-outline-none focus-visible:tw-outline-focus'
          placeholder={t("paste_link_placeholder")}
          aria-label={t("paste_link")}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmit();
            }
          }}
        />
      </div>
      <div className='tw-flex tw-items-center tw-gap-2'>
        <Button className='tw-pointer-events-auto' variant='primary' size='md' onClick={onSubmit}>
          {t("transcribe")}
        </Button>
        <Button className='tw-pointer-events-auto' variant='elevated' size='md' onClick={onCancel}>
          {t("cancel")}
        </Button>
      </div>
    </div>
  );
}
